import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { api } from '../api';

export type Sticker = {
  id: string;
  code: string;
  team: string | null;
  kind: string;
  position: number;
  count: number;
};

export function useStickers(slug: string) {
  return useQuery({
    queryKey: ['stickers', slug],
    queryFn: () => api<{ stickers: Sticker[] }>(`/api/albums/${slug}/stickers`).then((r) => r.stickers),
    staleTime: 5 * 60_000,
  });
}

/**
 * Optimistic update + debounced PUT.
 * Pro Sticker eine eigene Debounce-Timer-Map, damit Taps auf unterschiedliche
 * Sticker sich nicht gegenseitig blockieren.
 */
export function useCycleStatus(slug: string) {
  const qc = useQueryClient();
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  // Beim Unmount alle Timer feuern (final flush)
  useEffect(() => {
    const map = timers.current;
    return () => {
      for (const t of map.values()) clearTimeout(t);
      map.clear();
    };
  }, []);

  return function cycle(code: string, currentCount: number): number {
    const nextCount = (currentCount + 1) % 3;

    // 1. Optimistic Update im Cache
    qc.setQueryData<Sticker[]>(['stickers', slug], (old) => {
      if (!old) return old;
      return old.map((s) => (s.code === code ? { ...s, count: nextCount } : s));
    });

    // 2. Debounce PUT (300ms)
    const prev = timers.current.get(code);
    if (prev) clearTimeout(prev);
    const t = setTimeout(() => {
      timers.current.delete(code);
      // finalen Cache-Wert lesen (kann durch weitere Taps verändert worden sein)
      const list = qc.getQueryData<Sticker[]>(['stickers', slug]) ?? [];
      const sticker = list.find((s) => s.code === code);
      const finalCount = sticker?.count ?? nextCount;

      void api<{ code: string; count: number }>(`/api/albums/${slug}/ownership/${code}`, {
        method: 'PUT',
        body: { count: finalCount },
      })
        .then(() => {
          // Bei Erfolg: Matches-Cache invalidieren (Score kann sich geändert haben)
          void qc.invalidateQueries({ queryKey: ['matches', slug], exact: false });
          void qc.invalidateQueries({ queryKey: ['albums'] });
        })
        .catch(() => {
          // Rollback: einfach neu laden — Quelle der Wahrheit ist Server
          void qc.invalidateQueries({ queryKey: ['stickers', slug] });
        });
    }, 300);
    timers.current.set(code, t);

    return nextCount;
  };
}
